import React, { useState, useEffect, useMemo } from 'react';
import { ToggleLeft, ToggleRight } from 'lucide-react';
import { trackEvent, EventName } from 'shared/lib/eventTracking';
import debounce from 'lodash/debounce';

const CollectiveImpactTable = () => {
  const [monthlyListeners, setMonthlyListeners] = useState('');
  const [contributionAmount, setContributionAmount] = useState(0.7);
  const [simulationResults, setSimulationResults] = useState<number[]>([]);
  const [viewMode, setViewMode] = useState<'table' | 'list'>('table');

  const listenerCounts = [5000, 20000, 100000, 500000];
  const percentages = [0.01, 0.02, 0.05, 0.1];

  const calculateImpact = (listeners: number, percentage: number) => {
    return (listeners * percentage * contributionAmount).toFixed(2);
  };

  const debouncedTrackEvent = useMemo(
    () => debounce((listeners: string, contribution: number) => {
      if (parseInt(listeners) > 0) {
        trackEvent(EventName.COLLECTIVE_TABLE_INTERACTION, {
          monthlyListeners: parseInt(listeners) || 0,
          contributionAmount: contribution
        });
      }
    }, 1250),
    []
  );

  useEffect(() => {
    if (monthlyListeners) {
      const listeners = parseInt(monthlyListeners);
      setSimulationResults(percentages.map(p => parseFloat(calculateImpact(listeners, p))));
    } else {
      setSimulationResults([]);
    }

    debouncedTrackEvent(monthlyListeners, contributionAmount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthlyListeners, contributionAmount, debouncedTrackEvent]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setViewMode('list');
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleMonthlyListenersChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMonthlyListeners(e.target.value);
  };

  const handleContributionAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setContributionAmount(parseFloat(e.target.value));
  };

  const TableView = () => (
    <div className="overflow-x-auto mt-5">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-editorial-muted/15">
            <th scope="col" className="px-2 py-2.5 text-left text-[11px] tracking-[0.14em] uppercase text-editorial-muted/55 font-normal">Listeners</th>
            {percentages.map((percentage) => (
              <th key={percentage} scope="col" className="px-2 py-2.5 text-right text-[11px] tracking-[0.14em] uppercase text-editorial-muted/55 font-normal">
                {(percentage * 100)}%
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {listenerCounts.map((listeners) => (
            <tr key={listeners} className="border-b border-editorial-muted/10">
              <td className="px-2 py-3 whitespace-nowrap font-cormorant text-editorial-muted">{listeners.toLocaleString()}</td>
              {percentages.map((percentage) => (
                <td key={percentage} className="px-2 py-3 whitespace-nowrap text-right font-cormorant text-editorial-muted tabular-nums">
                  ${calculateImpact(listeners, percentage)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const ListView = () => (
    <div className="mt-5 space-y-3">
      {listenerCounts.map((listeners) => (
        <div key={listeners} className="bg-ivory/60 border border-editorial-muted/10 p-4 rounded-2xl">
          <h4 className="font-cormorant text-editorial-muted text-lg mb-2">{listeners.toLocaleString()} Listeners</h4>
          <ul className="space-y-1.5">
            {percentages.map((percentage) => (
              <li key={percentage} className="flex justify-between text-sm text-editorial-muted/80">
                <span>{(percentage * 100)}%:</span>
                <span className="font-cormorant text-editorial-muted tabular-nums">${calculateImpact(listeners, percentage)}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );

  return (
    <div className="w-full bg-white rounded-3xl border border-editorial-muted/10 shadow-[0_24px_48px_-24px_rgba(12,19,45,0.12)] p-7 md:p-9 text-editorial-muted">
      <h2 className="font-cormorant text-editorial-muted text-2xl md:text-3xl leading-tight">Explore the Dynamics of Scale</h2>
      <p className="mt-2 text-editorial-muted/75 text-sm md:text-base leading-relaxed">
        This calculator shows monthly earnings based on a ${contributionAmount.toFixed(2)} contribution per listener.
      </p>

      <div className="mt-5 flex items-center gap-2">
        <button
          onClick={() => setViewMode(viewMode === 'table' ? 'list' : 'table')}
          aria-label="Toggle table or list view"
          className="text-editorial-accent hover:text-editorial-accent/80 transition-colors"
        >
          {viewMode === 'table' ? <ToggleLeft size={20} /> : <ToggleRight size={20} />}
        </button>
        <span className="text-[11px] tracking-[0.18em] uppercase text-editorial-muted/60">{viewMode === 'table' ? 'Table' : 'List'} View</span>
      </div>

      {viewMode === 'table' ? <TableView /> : <ListView />}

      <div className="mt-7 pt-7 border-t border-editorial-muted/10">
        <h3 className="font-cormorant text-editorial-muted text-xl md:text-2xl leading-tight">Estimate Your Coral Contributions</h3>
        <div className="mt-4 grid grid-cols-2 gap-4">
          <div className="flex flex-col">
            <label className="text-[11px] tracking-[0.14em] uppercase text-editorial-muted/60 mb-1.5" htmlFor="monthly-listeners">
              Monthly Listeners
            </label>
            <input
              id="monthly-listeners"
              type="number"
              value={monthlyListeners}
              onChange={handleMonthlyListenersChange}
              className="w-full bg-transparent border-b border-editorial-muted/20 focus:border-editorial-muted/60 focus:outline-none transition-colors py-1.5 font-cormorant text-lg tabular-nums text-editorial-muted"
              placeholder="Listeners"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-[11px] tracking-[0.14em] uppercase text-editorial-muted/60 mb-1.5" htmlFor="contribution-amount">
              Contribution ($)
            </label>
            <input
              id="contribution-amount"
              type="number"
              value={contributionAmount}
              onChange={handleContributionAmountChange}
              step="0.1"
              min="0"
              className="w-full bg-transparent border-b border-editorial-muted/20 focus:border-editorial-muted/60 focus:outline-none transition-colors py-1.5 font-cormorant text-lg tabular-nums text-editorial-muted"
              placeholder="Amount"
            />
          </div>
        </div>
        {simulationResults.length > 0 && (
          <div className="overflow-x-auto mt-5">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-editorial-ink text-ivory">
                  <th scope="col" className="px-2 py-2.5 text-left text-[11px] tracking-[0.14em] uppercase font-normal">Listeners %</th>
                  {percentages.map((percentage) => (
                    <th key={percentage} scope="col" className="px-2 py-2.5 text-right text-[11px] tracking-[0.14em] uppercase font-normal">
                      {(percentage * 100)}%
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-editorial-muted/10">
                  <td className="px-2 py-3 whitespace-nowrap font-cormorant text-editorial-muted">{parseInt(monthlyListeners).toLocaleString()}</td>
                  {simulationResults.map((result, index) => (
                    <td key={index} className="px-2 py-3 whitespace-nowrap text-right font-cormorant text-editorial-accent tabular-nums">
                      ${result}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CollectiveImpactTable;